import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchSharedData } from "./UAFetchSharedData";
import "../../Styles/UASharedContent.css";
import Carousel from "../../../../common/Carousel/Carousel";
import GridLayout from "../../../../common/Grid/gridMode";
import LightboxGallery from "../../../../common/LightBox/lightBox";
import HorizontalScrollDisplay from "../../../../common/HorizontalScroll/horizontalScroll";
import VerticalScroll from "../../../../common/VerticalScroll/verticalScroll";
import "../../../Styles/common-styles.css";
import "../../Styles/UACommonStyles.css";

// Platform Color Mapping
const PLATFORM_COLORS = {
  instagram: {
    background: "none",
    color: "linear-gradient(135deg, #E1306C, #C13584, #833AB4)",
    border: "darkslategray",
    icon: "📷",
  },
  facebook: {
    background: "none",
    color: "#1877F2",
    border: "darkslategray",
    icon: "👥",
  },
  linkedin: {
    background: "none",
    color: "#0077B5",
    border: "darkslategray",
    icon: "💼",
  },
  snapchat: {
    background: "none",
    color: "#FFFC00",
    border: "darkslategray",
    icon: "👻",
  },
  tiktok: {
    background: "none",
    color: "linear-gradient(135deg, #010101, #69C9D0, #EE1D52)",
    border: "#69C9D0",
    icon: "🎵",
  },
  pinterest: {
    background: "#E60023",
    color: "#FFFFFF",
    border: "#E60023",
    icon: "📌",
  },
  twitter: {
    background: "#1DA1F2",
    color: "#FFFFFF",
    border: "#1DA1F2",
    icon: "🐦",
    shadow: "rgba(29, 161, 242, 0.4)"
  },
  x: {
    background: "#000000",
    color: "#FFFFFF",
    border: "#FFFFFF",
    icon: "✖️",
    shadow: "rgba(255, 255, 255, 0.3)"
  },
  whatsapp: {
    background: "#25D366",
    color: "#FFFFFF",
    border: "#25D366",
    icon: "💬",
    shadow: "rgba(37, 211, 102, 0.4)"
  },
  youtube: {
    background: "#FF0000",
    color: "#FFFFFF",
    border: "#FF0000",
    icon: "▶️",
    shadow: "rgba(255, 0, 0, 0.4)"
  },
  reddit: {
    background: "#FF4500",
    color: "#FFFFFF",
    border: "#FF4500",
    icon: "🤖",
    shadow: "rgba(255, 69, 0, 0.4)"
  },
  telegram: {
    background: "#0088CC",
    color: "#FFFFFF",
    border: "#0088CC",
    icon: "✈️",
    shadow: "rgba(0, 136, 204, 0.4)"
  },
  discord: {
    background: "#5865F2",
    color: "#FFFFFF",
    border: "#5865F2",
    icon: "🎮",
    shadow: "rgba(88, 101, 242, 0.4)"
  },
  threads: {
    background: "linear-gradient(135deg, #000000, #1A1A1A)",
    color: "#FFFFFF",
    border: "#FFFFFF",
    icon: "🧵",
    shadow: "rgba(255, 255, 255, 0.2)"
  },
  bluesky: {
    background: "#1185FE",
    color: "#FFFFFF",
    border: "#1185FE",
    icon: "🦋",
    shadow: "rgba(17, 133, 254, 0.4)"
  },
  mastodon: {
    background: "#6364FF",
    color: "#FFFFFF",
    border: "#6364FF",
    icon: "🐘",
    shadow: "rgba(99, 100, 255, 0.4)"
  }
};

const UASharedContent = ({ mediaDisplayMode }) => {
  const [sharedData, setSharedData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSharedData().then((data) => {
      setSharedData(data);
      setLoading(false);
    });
  }, []);

  // Helper function to get platform badge style
  const getPlatformStyle = (platform) => {
    const platformKey = platform.toLowerCase();
    return PLATFORM_COLORS[platformKey] || {
      background: "#6C757D",
      color: "#FFFFFF",
      border: "#6C757D",
      icon: "🌐",
      shadow: "rgba(108, 117, 125, 0.4)"
    };
  };

  // Helper function to parse sharedOn string into array
  const parseSharedOn = (sharedOn) => {
    if (!sharedOn) return [];
    
    // If it's already an array, return it
    if (Array.isArray(sharedOn)) return sharedOn;
    
    // If it's a string, split by comma and clean up
    if (typeof sharedOn === 'string') {
      return sharedOn
        .split(',')
        .map(platform => platform.trim())
        .filter(platform => platform.length > 0);
    }
    
    return [];
  };

  if (loading) {
    return (
      <div className="uash-loading">
        <p className="text-header">Loading shared posts...</p>
      </div>
    );
  }

  return (
    <div className="uash-shared-feed-container">
      {sharedData && sharedData.length > 0 ? (
        sharedData.map((user) =>
          user.posts && user.posts.length > 0 ? (
            user.posts.map((post) => {
              const sharedPlatforms = parseSharedOn(post.sharedOn);
              return (
                <div key={post.postId} className="uash-post-wrapper">
                  {/* Header Section - Black Background */}
                  <div className="uash-post-header-section">
                    {/* User Info */}
                    <div className="uash-user-info-wrapper">
                      <div className="uash-user-avatar-wrapper">
                        <img
                          src={post.userimage}
                          alt="User"
                          className="tt-profile-image"
                        />
                      </div>

                      <div className="uash-user-details">
                        <div className="uash-user-name-row">
                          <Link
                            to={`/user-account/${post.userId}`}
                            className="uash-user-name-link"
                          >
                            <span className="text-header">{post.userName}</span>
                          </Link>

                          {/* Comma Separator */}
                          {sharedPlatforms.length > 0 && (
                            <span className="uash-comma-separator">,</span>
                          )}

                          {/* Inline Platform Badges */}
                          {sharedPlatforms.length > 0 && (
                            <div className="uash-inline-platforms">
                              {sharedPlatforms.map((platform, index) => {
                                const platformStyle = getPlatformStyle(platform);
                                return (
                                  <div
                                    key={index}
                                    className="uash-platform-badge-inline"
                                    style={{
                                      background: platformStyle.background,
                                      color: platformStyle.color,
                                      borderColor: platformStyle.border,
                                    }}
                                    title={`Shared on ${platform.charAt(0).toUpperCase() + platform.slice(1)}`}
                                  >
                                    <span className="uash-platform-icon-inline">
                                      {platformStyle.icon}
                                    </span>
                                    <span className="uash-platform-name-inline">
                                      {platform.charAt(0).toUpperCase() + platform.slice(1)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Timestamp on Next Line */}
                        <div className="uash-timestamp-row">
                          <span className="text-label uash-timestamp">
                            {new Date(post.timestamp).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Platform Badges Section - Color Coded */}
                    {post.sharedOn && Array.isArray(post.sharedOn) && post.sharedOn.length > 0 && (
                      <div className="uash-platforms-section">
                        <div className="uash-platforms-label">
                          <span className="uash-shared-text">✓ Shared on:</span>
                        </div>
                        <div className="uash-platforms-badges">
                          {post.sharedOn.map((platform, index) => {
                            const platformStyle = getPlatformStyle(platform);
                            return (
                              <div
                                key={index}
                                className="uash-platform-badge"
                                style={{
                                  background: platformStyle.background,
                                  color: platformStyle.color,
                                  borderColor: platformStyle.border,
                                  boxShadow: `0 4px 12px ${platformStyle.shadow}`
                                }}
                                title={`Shared on ${platform.charAt(0).toUpperCase() + platform.slice(1)}`}
                              >
                                <span className="uash-platform-icon">
                                  {platformStyle.icon}
                                </span>
                                <span className="uash-platform-name">
                                  {platform.charAt(0).toUpperCase() + platform.slice(1)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Post Card - Content Only */}
                  <div className="uash-shared-feed-card">
                    {/* Description */}
                    <div className="uash-post-description">
                      <p className="text-label">{post.descriptions}</p>
                    </div>

                    {/* Media Content */}
                    {Array.isArray(post.medias) && post.medias.length > 0 && (
                      <div className="uash-shared-feed-media">
                        <div className="uash-shared-media-container">
                          {mediaDisplayMode === "slideshow" ? (
                            <Carousel items={post.medias} />
                          ) : mediaDisplayMode === "lightbox" ? (
                            <LightboxGallery mediaFiles={post.medias} />
                          ) : mediaDisplayMode === "horizontal-scroll" ? (
                            <HorizontalScrollDisplay medias={post.medias} />
                          ) : mediaDisplayMode === "vertical-scroll" ? (
                            <VerticalScroll medias={post.medias} />
                          ) : (
                            <GridLayout mediaFiles={post.medias} />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Footer with Likes and Location */}
                    <div className="uash-post-footer">
                      <div className="uash-footer-stats">
                        <span className="uash-likes-count">
                          👍 {post.nlikes || 0} Likes
                        </span>
                        <span className="uash-separator">|</span>
                        <Link
                          to={`/location-page/${post.location}`}
                          className="uash-location-link"
                        >
                          📍 {post.location}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          ) : null
        )
      ) : (
        <div className="uash-no-posts">
          <p className="text-label">No shared posts found.</p>
        </div>
      )}
    </div>
  );
};

export default UASharedContent;
